import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate, Link } from 'react-router-dom';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import LoadingSpinner from '../../components/ui/LoadingSpinner';

const LoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  const { signIn, user, userProfile } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (user && userProfile) {
      // Role-based routing: admin/editor/talent → dashboard, user → the-stage-student-dashboard
      const userRole = userProfile?.role?.toLowerCase();
      
      if (userRole === 'superuser' || userRole === 'editor' || userRole === 'talent') {
        navigate('/dashboard');
      } else {
        // Regular users (students) go to The Stage student dashboard
        navigate('/the-stage-student-dashboard');
      }
    }
  }, [user, userProfile, navigate]);

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setError('');
    setLoading(true);

    try {
      const { error } = await signIn(email, password);
      
      if (error) {
        setError(error?.message || 'Failed to sign in');
      }
    } catch (err) {
      setError('An unexpected error occurred');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-900 via-blue-900 to-purple-900 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div>
          {/* RoleCall Logo */}
          <div className="mx-auto h-40 w-40 flex items-center justify-center mb-6">
            <img 
              src="/assets/images/rolecall_logo-1756785358409.png" 
              alt="RoleCall Logo" 
              className="h-full w-auto object-contain"
            />
          </div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-white">
            Welcome to RoleCall
          </h2>
          <p className="mt-2 text-center text-sm text-blue-100">
            Sign in to your account
          </p>
        </div>
        
        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          {error && (
            <div className="bg-red-900 bg-opacity-20 border border-red-500 rounded-md p-4">
              <p className="text-sm text-red-200">{error}</p>
            </div>
          )}
          
          <div className="space-y-4">
            <Input
              id="email"
              name="email"
              type="email"
              autoComplete="email"
              required
              placeholder="Email address"
              value={email}
              onChange={(e) => setEmail(e?.target?.value)}
              disabled={loading}
              className="bg-white bg-opacity-10 border-white border-opacity-20 text-white placeholder-blue-200 focus:ring-blue-400 focus:border-blue-400"
            />
            
            <Input
              id="password"
              name="password"
              type="password"
              autoComplete="current-password"
              required
              placeholder="Password"
              value={password}
              onChange={(e) => setPassword(e?.target?.value)}
              disabled={loading}
              className="bg-white bg-opacity-10 border-white border-opacity-20 text-white placeholder-blue-200 focus:ring-blue-400 focus:border-blue-400"
            />
          </div>

          <div className="flex items-center justify-between">
            <Link 
              to="/auth/forgot-password"
              className="text-sm text-blue-200 hover:text-white"
            >
              Forgot your password?
            </Link>
          </div>

          <Button
            type="submit"
            className="w-full bg-white text-indigo-900 hover:bg-gray-100"
            disabled={loading}
          >
            {loading ? <LoadingSpinner size="sm" /> : 'Sign in'}
          </Button>

          <div className="text-center">
            <p className="text-sm text-blue-100">
              Don't have an account?{' '}
              <Link 
                to="/auth/register"
                className="font-medium text-white hover:text-blue-200"
              >
                Register here
              </Link>
            </p>
            <p className="text-sm text-blue-100 mt-2">
              <Link 
                to="/"
                className="font-medium text-white hover:text-blue-200"
              >
                Back Home
              </Link>
            </p>
          </div>
        </form>
      </div>
    </div>
  );
};

export default LoginPage;